import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Button, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { useRealtimeConnection } from '../../../lib/events';
import { useWorkspace } from '../../../lib/session';
import { consentBodyFor, fetchRoomToken, useMeeting, useMeetingActions } from '../api';
import { LobbyView } from '../lobby/LobbyView';
import { useLocalMedia } from '../media';
import { getRoomClient, type BrimoRoomClient, type BrimoRoomParticipant } from '../room-client';
import { CallView, type CallConnection } from './CallView';
import styles from './RoomPage.module.css';

const DISCONNECTED_MESSAGE = 'Die Verbindung zum Meetingraum ist unterbrochen.';

function isPickerCancel(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError');
}

/**
 * `/calendar/room/:meetingId` — full screen outside the shell (entered by a document navigation, so that the
 * camera/microphone policy of this path applies): Beratung 10 while the room is scheduled or in the lobby,
 * Beratung 11/12/14 while it runs; an ended conversation continues on its review.
 */
export function RoomPage() {
  const { meetingId = '' } = useParams();
  useRealtimeConnection();
  const workspace = useWorkspace();
  const hostName = `${workspace.user.firstName} ${workspace.user.lastName}`.trim();
  const navigate = useNavigate();
  const { toast } = useToast();
  const meeting = useMeeting(meetingId);
  const actions = useMeetingActions(meetingId);
  const status = meeting.data?.status ?? null;
  const ended = status === 'ended' || status === 'processed';
  const inCall = status === 'running';
  const media = useLocalMedia(status !== null && !inCall && !ended);

  // „Raum öffnen": the host enters the lobby (idempotent while lobby/running).
  const openedRef = useRef(false);
  const openRoom = actions.open.mutate;
  useEffect(() => {
    if (status === null || ended || openedRef.current) return;
    openedRef.current = true;
    openRoom(undefined, {
      onError: (error) => {
        if (error instanceof ApiError && error.status === 409) void meeting.refetch();
        else toast({ message: errorMessage(error) });
      },
    });
  }, [status, ended, openRoom, meeting, toast]);

  // --- room client -----------------------------------------------------------------------------------
  const clientRef = useRef<BrimoRoomClient | null>(null);
  const unsubscribeRef = useRef<Array<() => void>>([]);
  const leavingRef = useRef(false);
  const shareStartingRef = useRef(false);
  const devicesRef = useRef<{ cameraId: string | null; microphoneId: string | null }>({
    cameraId: null,
    microphoneId: null,
  });
  const [participants, setParticipants] = useState<BrimoRoomParticipant[]>([]);
  const [connection, setConnection] = useState<CallConnection>({ state: 'connecting', error: null });
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [shareLabel, setShareLabel] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  if (!inCall) devicesRef.current = { cameraId: media.cameraId, microphoneId: media.microphoneId };

  const release = useCallback(async () => {
    for (const unsubscribe of unsubscribeRef.current) unsubscribe();
    unsubscribeRef.current = [];
    const client = clientRef.current;
    clientRef.current = null;
    if (client) await client.disconnect().catch(() => undefined);
  }, []);

  const connect = useCallback(async () => {
    if (clientRef.current) return;
    const client = getRoomClient();
    clientRef.current = client;
    // a client released while it was still connecting (unmount, end) never becomes the current one again
    const stale = () => clientRef.current !== client;
    setConnection({ state: 'connecting', error: null });
    try {
      const credentials = await fetchRoomToken(meetingId);
      if (stale()) return;
      const sync = () => setParticipants(client.participants());
      unsubscribeRef.current = [
        client.on('participants', sync),
        client.on('activeSpeaker', sync),
        client.on('disconnected', () => {
          if (leavingRef.current || stale()) return;
          clientRef.current = null;
          setShareLabel(null);
          setConnection({ state: 'failed', error: DISCONNECTED_MESSAGE });
        }),
      ];
      await client.connect(credentials);
      if (stale()) {
        await client.disconnect().catch(() => undefined);
        return;
      }
      const { cameraId, microphoneId } = devicesRef.current;
      await client.setMicrophone(true, microphoneId ?? undefined);
      await client.setCamera(true, cameraId ?? undefined);
      setMicOn(true);
      setCamOn(true);
      sync();
      setConnection({ state: 'connected', error: null });
    } catch (error) {
      if (stale()) return;
      await release();
      setConnection({ state: 'failed', error: errorMessage(error) });
    }
  }, [meetingId, release]);

  // A running conversation connects on its own (after „Gespräch starten" and after a reload) and leaves
  // the room when it stops running or the page goes away.
  useEffect(() => {
    if (!inCall) return;
    leavingRef.current = false;
    void connect();
    return () => {
      leavingRef.current = true;
      void release();
    };
  }, [inCall, connect, release]);

  // The browser's own „stop sharing" ends the share as well.
  const local = participants.find((participant) => participant.isLocal) ?? null;
  const reportShare = actions.screenShare.mutate;
  useEffect(() => {
    if (shareLabel === null || shareStartingRef.current || local === null || local.screenTrack !== null)
      return;
    reportShare({ state: 'ended', label: shareLabel });
    setShareLabel(null);
  }, [shareLabel, local, reportShare]);

  // The room was closed elsewhere (LiveKit `room_finished`, another tab): continue on the review.
  if (ended) return <Navigate to={`/calendar/room/${meetingId}/review`} replace />;

  if (meeting.isError) {
    return (
      <div className={styles.state}>
        <h1 className={styles.stateTitle}>{errorMessage(meeting.error)}</h1>
        <Button variant="ghostInverse" onClick={() => window.location.assign('/calendar')}>
          Zur Beratung
        </Button>
      </div>
    );
  }
  if (!meeting.data) {
    return (
      <div className={styles.state} aria-busy="true" aria-label="Wird geladen">
        <Skeleton width={320} height={20} />
        <Skeleton width={520} height={260} />
      </div>
    );
  }
  const view = meeting.data;

  // A failed start leaves the button usable (the banner stays until the next attempt); the view is read again,
  // so the lobby asks for what the meeting needs now (e.g. the four items once its type records).
  async function start() {
    setStartError(null);
    try {
      await actions.start.mutateAsync();
    } catch (error) {
      setStartError(errorMessage(error));
      void meeting.refetch();
    }
  }

  async function toggleMic() {
    const client = clientRef.current;
    if (!client) return;
    const next = !micOn;
    try {
      await client.setMicrophone(next, devicesRef.current.microphoneId ?? undefined);
      setMicOn(next);
    } catch (error) {
      toast({ message: errorMessage(error) });
    }
  }

  async function toggleCam() {
    const client = clientRef.current;
    if (!client) return;
    const next = !camOn;
    try {
      await client.setCamera(next, devicesRef.current.cameraId ?? undefined);
      setCamOn(next);
    } catch (error) {
      toast({ message: errorMessage(error) });
    }
  }

  async function share(label: string): Promise<boolean> {
    const client = clientRef.current;
    if (!client) return false;
    shareStartingRef.current = true;
    try {
      if (shareLabel !== null) {
        await client.stopScreenShare();
        reportShare({ state: 'ended', label: shareLabel });
      }
      await client.startScreenShare();
      setShareLabel(label);
      reportShare({ state: 'started', label });
      return true;
    } catch (error) {
      if (shareLabel !== null) setShareLabel(null);
      if (isPickerCancel(error)) return false;
      throw error;
    } finally {
      shareStartingRef.current = false;
    }
  }

  async function stopShare() {
    const client = clientRef.current;
    const label = shareLabel;
    if (!client || label === null) return;
    shareStartingRef.current = true;
    try {
      await client.stopScreenShare();
    } finally {
      shareStartingRef.current = false;
    }
    setShareLabel(null);
    reportShare({ state: 'ended', label });
  }

  async function end() {
    leavingRef.current = true;
    try {
      await actions.end.mutateAsync();
      await release();
      void navigate(`/calendar/room/${meetingId}/review`, { replace: true });
    } catch (error) {
      leavingRef.current = false;
      toast({ message: errorMessage(error) });
    }
  }

  if (inCall) {
    return (
      <CallView
        meeting={view}
        hostName={hostName}
        participants={participants}
        connection={connection}
        onReconnect={() => void connect()}
        micOn={micOn}
        camOn={camOn}
        onToggleMic={() => void toggleMic()}
        onToggleCam={() => void toggleCam()}
        shareLabel={shareLabel}
        onShare={share}
        onStopShare={stopShare}
        onEnd={end}
        ending={actions.end.isPending}
      />
    );
  }

  return (
    <LobbyView
      meeting={view}
      media={media}
      hostName={hostName}
      onConsent={() => actions.consent.mutate(consentBodyFor(view.consent.items))}
      consentPending={actions.consent.isPending}
      consentError={actions.consent.error}
      onAdmit={(participantId) =>
        actions.admit.mutate(participantId, { onError: (error) => toast({ message: errorMessage(error) }) })
      }
      admittingId={actions.admit.isPending ? (actions.admit.variables ?? null) : null}
      onStart={() => void start()}
      starting={actions.start.isPending}
      startError={startError}
    />
  );
}
